import React, { useState, useRef } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Upload, Loader2, CheckCircle2, ShieldAlert, FileWarning, RefreshCw
} from "lucide-react";

const MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ["application/pdf", "image/png", "image/jpeg"];

/**
 * S3 Quarantine Upload component.
 *
 * Flow: select file → presign → PUT to S3 → confirm magic bytes → poll scan status
 *
 * Props:
 *  - category: "nf" | "comprovante" | "contrato"
 *  - onComplete: (uploadRecord) => void
 *  - label: button label (default "Anexar NF")
 *  - buttonVariant: shadcn variant (default "outline")
 *  - buttonSize: shadcn size (default "sm")
 *  - disabled: boolean
 */
export default function FileUpload({
  category = "nf",
  onComplete,
  label = "Anexar NF",
  buttonVariant = "outline",
  buttonSize = "sm",
  disabled = false,
}) {
  const [state, setState] = useState("idle"); // idle | selecting | uploading | confirming | scanning | done | error
  const [progress, setProgress] = useState(0);
  const [uploadRecord, setUploadRecord] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Cleanup polling on unmount
  React.useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const reset = () => {
    setState("idle");
    setProgress(0);
    setUploadRecord(null);
    setErrorMsg("");
    if (pollRef.current) clearInterval(pollRef.current);
  };

  const handleClick = () => {
    if (state === "error") {
      reset();
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("Apenas arquivos PDF, PNG ou JPEG são permitidos");
      return;
    }
    if (file.size > MAX_SIZE) {
      toast.error("Arquivo deve ter no máximo 5MB");
      return;
    }
    if (/[<>{}|\\^`]/.test(file.name)) {
      toast.error("Nome de arquivo contém caracteres inválidos");
      return;
    }

    await uploadFile(file);
  };

  const uploadFile = async (file) => {
    try {
      // Step 1: Get presigned URL
      setState("uploading");
      setProgress(10);
      const { data: presign } = await api.post("/uploads/presign", {
        filename: file.name,
        content_type: file.type,
        file_size: file.size,
        category,
      });
      setProgress(25);

      // Step 2: Upload directly to S3
      const xhr = new XMLHttpRequest();
      await new Promise((resolve, reject) => {
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            setProgress(25 + Math.round((evt.loaded / evt.total) * 50));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`S3 upload failed: ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error("Erro de conexão com o serviço de upload"));
        xhr.open("PUT", presign.upload_url);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.send(file);
      });
      setProgress(80);

      // Step 3: Confirm upload (magic bytes validation)
      setState("confirming");
      const { data: confirm } = await api.post("/uploads/confirm", {
        s3_key: presign.quarantine_key,
      });
      setProgress(90);

      // Step 4: Start polling scan status
      setState("scanning");
      setUploadRecord({ ...confirm, upload_id: presign.upload_id });

      pollRef.current = setInterval(async () => {
        try {
          const { data: status } = await api.get(`/uploads/status/${presign.upload_id}`);
          if (status.scan_status === "clean") {
            clearInterval(pollRef.current);
            setState("done");
            setProgress(100);
            setUploadRecord(status);
            toast.success("Arquivo enviado e validado com sucesso");
            onComplete?.(status);
          } else if (status.scan_status === "infected") {
            clearInterval(pollRef.current);
            setState("error");
            setErrorMsg("Arquivo detectado como malicioso e removido.");
            toast.error("Arquivo rejeitado — conteúdo suspeito detectado");
          } else if (status.scan_status === "rejected") {
            clearInterval(pollRef.current);
            setState("error");
            setErrorMsg("Arquivo rejeitado na validação.");
          }
        } catch (e) {
          if (process.env.NODE_ENV === "development") console.warn("Scan poll error:", e);
          // Keep polling
        }
      }, 5000);

      // Auto-stop polling after 2 minutes and mark as done (scan might take time)
      setTimeout(() => {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          if (stateRef.current === "scanning") {
            setState("done");
            setProgress(100);
            toast.success("Arquivo enviado — escaneamento de segurança em andamento");
            onComplete?.({ upload_id: presign.upload_id });
          }
        }
      }, 120000);

    } catch (err) {
      setState("error");
      const detail = err?.response?.data?.detail;
      const msg = detail ? formatApiErrorDetail(detail) : (err.message || "Erro ao enviar arquivo");
      setErrorMsg(msg);
      toast.error(msg);
    }
  };

  const stateConfig = {
    idle: { icon: Upload, text: label, spinning: false },
    uploading: { icon: Loader2, text: `Enviando… ${progress}%`, spinning: true },
    confirming: { icon: Loader2, text: "Validando…", spinning: true },
    scanning: { icon: ShieldAlert, text: "Escaneando…", spinning: false },
    done: { icon: CheckCircle2, text: "Enviado", spinning: false },
    error: { icon: FileWarning, text: "Tentar novamente", spinning: false },
  };

  const { icon: Icon, text, spinning } = stateConfig[state] || stateConfig.idle;
  const isWorking = ["uploading", "confirming", "scanning"].includes(state);

  return (
    <div className="inline-flex items-center gap-2" data-testid="file-upload-component">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg"
        className="hidden"
        onChange={handleFileSelect}
        data-testid="file-upload-input"
      />

      {state === "done" ? (
        <div className="flex items-center gap-1.5 text-xs text-emerald-700 font-semibold" data-testid="upload-done">
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span>Enviado</span>
        </div>
      ) : (
        <Button
          variant={state === "error" ? "destructive" : buttonVariant}
          size={buttonSize}
          onClick={handleClick}
          disabled={disabled || isWorking}
          data-testid="file-upload-button"
        >
          <Icon className={`h-3.5 w-3.5 mr-1.5 ${spinning ? "animate-spin" : ""}`} />
          {text}
        </Button>
      )}

      {isWorking && (
        <div className="w-20 h-1.5 bg-zinc-200 rounded-full overflow-hidden" data-testid="upload-progress-bar">
          <div
            className="h-full bg-[#0055FF] rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {state === "scanning" && (
        <span className="text-xs text-amber-600 flex items-center gap-1" data-testid="upload-scanning">
          <ShieldAlert className="h-3 w-3" /> Verificação de segurança
        </span>
      )}
    </div>
  );
}
